import type { SessionConfig, TerminalState } from './types';
import { SessionState, isTerminal } from './types';
import type { ServerDirective } from '@/lib/directives';
import { DirectiveKind } from '@/lib/directives';
import { logger } from '@/lib/utils/logger';

/** Schedule the proactive refresh at this fraction of the access-token TTL. */
const REFRESH_AT_FRACTION = 0.8;

/** Fallback access-token TTL (seconds) when the refresh endpoint omits expiresIn. */
const DEFAULT_ACCESS_TTL_SEC = 900;

/**
 * Per-page-load random token appended to the probe URL so the boot probe
 * is guaranteed a cache miss — defense beyond `cache: 'no-store'` for
 * browsers, intermediate proxies, or service workers that ignore
 * Cache-Control on cached responses from a prior session. New tab / new
 * load gets a new token, so a stale cached entry from a prior tenant or
 * a pre-deploy build is unreachable. The backend ignores unknown query
 * params, so this is purely a client-side cache-key bust.
 */
const PROBE_CACHE_BUSTER: string = (() => {
  try {
    return crypto.randomUUID().replace(/-/g, '').slice(0, 16);
  } catch {
    return Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
  }
})();

/** Append the per-load cache buster as a query param, preserving any existing query string. */
function withCacheBuster(url: string): string {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}_=${PROBE_CACHE_BUSTER}`;
}

/**
 * Reads `detail.error_type` from a JSON error response body. Returns null on
 * parse failure or shape mismatch. Used by probe() to detect the backend's
 * expired-access-token recovery signal.
 */
async function readErrorTypeFromBody(response: Response): Promise<string | null> {
  const body = (await response
    .clone()
    .json()
    .catch(() => null)) as unknown;
  if (body === null || typeof body !== 'object') return null;
  const { detail } = body as { detail?: unknown };
  if (detail === null || detail === undefined || typeof detail !== 'object') {
    return null;
  }
  const { error_type: errorType } = detail as { error_type?: unknown };
  return typeof errorType === 'string' ? errorType : null;
}

export class SessionManager {
  private _state: SessionState = SessionState.Unknown;
  private readonly _config: SessionConfig;
  private _proactiveTimer: ReturnType<typeof setTimeout> | null = null;
  private _proactiveDueAt: number | null = null;
  private _visibilityHandler: (() => void) | null = null;
  private _directiveSubscribers = new Set<(d: ServerDirective) => void>();
  private _stateSubscribers = new Set<(s: SessionState) => void>();
  private _refreshInFlight: Promise<boolean> | null = null;

  constructor(config: SessionConfig) {
    this._config = config;
    this.installVisibilityHandler();
  }

  private installVisibilityHandler(): void {
    if (this._visibilityHandler !== null) return;
    this._visibilityHandler = () => {
      this.onVisibilityChange();
    };
    document.addEventListener('visibilitychange', this._visibilityHandler);
  }

  /**
   * Subscribe to directives emitted by this SessionManager. In Phase 1
   * the only kind emitted here is `session_expired` (on refresh failure).
   * Returns an unsubscribe function.
   */
  onDirective(cb: (d: ServerDirective) => void): () => void {
    this._directiveSubscribers.add(cb);
    return () => {
      this._directiveSubscribers.delete(cb);
    };
  }

  private emitDirective(d: ServerDirective): void {
    // Snapshot first so a subscriber that calls onDirective during dispatch
    // doesn't see its own callback fired for this same directive.
    for (const cb of [...this._directiveSubscribers]) {
      try {
        cb(d);
      } catch (err) {
        // Subscriber errors must not interfere with state-machine progress,
        // but they shouldn't vanish silently — a broken DirectiveHandler is
        // exactly what we'd want visibility into.
        logger.warn('SessionManager directive subscriber threw', {
          domain: this._config.domain,
          kind: d.kind,
          err,
        });
      }
    }
  }

  /**
   * Internal state setter that dispatches to subscribers on every
   * transition. All places that previously wrote `this._state = ...`
   * must go through this so subscriptions (and awaitReady) see the
   * change.
   */
  private setState(next: SessionState): void {
    if (this._state === next) return;
    const prev = this._state;
    this._state = next;
    // Instrumentation (CU-868kygk35): session-state transitions are the ground
    // truth for diagnosing spurious logouts. Logged at info so they surface in
    // the browser console during a staging reproduction alongside the
    // AuthErrorBoundary decision log.
    logger.info('[session] state transition', {
      domain: this._config.domain,
      from: prev,
      to: next,
    });
    // Snapshot so a subscriber that calls onStateChange during dispatch
    // doesn't see its own callback fired for this transition.
    for (const cb of [...this._stateSubscribers]) {
      try {
        cb(next);
      } catch (err) {
        logger.warn('SessionManager state subscriber threw', {
          domain: this._config.domain,
          state: next,
          err,
        });
      }
    }
  }

  /**
   * Subscribe to state changes. Returns an unsubscribe function.
   * Consumed by AuthContext / AdminAuthContext in Phase 2 to mirror
   * SessionManager state into React.
   */
  onStateChange(cb: (state: SessionState) => void): () => void {
    this._stateSubscribers.add(cb);
    return () => {
      this._stateSubscribers.delete(cb);
    };
  }

  /**
   * Resolves to the current TerminalState (alive/anonymous/dead). If the
   * SessionManager is in a transient state (unknown/probing/refreshing),
   * queues until the next terminal transition. ApiClient's request flow
   * uses this in Phase 2 to gate non-public calls.
   */
  async awaitReady(): Promise<TerminalState> {
    if (isTerminal(this._state)) {
      return this._state;
    }
    return new Promise<TerminalState>((resolve) => {
      const unsubscribe = this.onStateChange((s) => {
        if (isTerminal(s)) {
          unsubscribe();
          resolve(s);
        }
      });
    });
  }

  private markRefreshFailed(): void {
    if (this._state === SessionState.Dead) return; // idempotent — dead is terminal
    this.clearProactiveTimer();
    this.setState(SessionState.Dead);
    this.emitDirective({
      kind: DirectiveKind.SessionExpired,
      message: 'Your session has expired. Please log in again to continue.',
      domain: this._config.domain,
      redirectTo: this._config.loginRedirectUrl,
    });
  }

  private scheduleProactiveRefresh(expiresInSec: number): void {
    if (this._proactiveTimer !== null) {
      clearTimeout(this._proactiveTimer);
    }
    // Fire at REFRESH_AT_FRACTION of the token's lifetime so the cookie
    // rotates before any request can 401 due to expiry.
    const fireInMs = expiresInSec * 1000 * REFRESH_AT_FRACTION;
    this._proactiveDueAt = Date.now() + fireInMs;
    this._proactiveTimer = setTimeout(() => {
      this._proactiveTimer = null;
      this._proactiveDueAt = null;
      void this.runProactiveRefresh();
    }, fireInMs);
  }

  private clearProactiveTimer(): void {
    if (this._proactiveTimer !== null) {
      clearTimeout(this._proactiveTimer);
      this._proactiveTimer = null;
    }
    this._proactiveDueAt = null;
  }

  // Backgrounded tabs throttle setTimeout, so the proactive timer may fire
  // late or not at all. On visibility regain, if the wall-clock due-at has
  // passed, refresh immediately. No-op when state !== 'alive' or when no
  // refresh is currently scheduled.
  private onVisibilityChange(): void {
    if (document.visibilityState !== 'visible') return;
    if (this._state !== SessionState.Alive) return;
    if (this._proactiveDueAt === null) return;
    if (Date.now() < this._proactiveDueAt) return;

    // Window has passed while the tab was backgrounded; refresh now.
    this.clearProactiveTimer();
    // Use the shared in-flight slot so a concurrent anomaly call dedupes.
    if (this._refreshInFlight !== null) return;
    this._refreshInFlight = this.executeRefresh();
    void this._refreshInFlight.finally(() => {
      this._refreshInFlight = null;
    });
  }

  private async runProactiveRefresh(): Promise<void> {
    if (this._state !== SessionState.Alive) return; // safety: timer fired during transition
    if (this._refreshInFlight !== null) return; // anomaly path already covered it
    this._refreshInFlight = this.executeRefresh();
    try {
      await this._refreshInFlight;
    } finally {
      this._refreshInFlight = null;
    }
  }

  private async executeRefresh(): Promise<boolean> {
    // Guard so executeRefresh can be safely called from a non-alive state
    // (e.g., a second concurrent caller arriving after markRefreshFailed
    // already transitioned to dead). Never downgrade dead → refreshing.
    if (this._state === SessionState.Alive) this.setState(SessionState.Refreshing);
    try {
      const response = await fetch(this._config.refreshEndpoint, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        const data = (await response.json().catch(() => ({}))) as { expiresIn?: number };
        this.setState(SessionState.Alive);
        this.scheduleProactiveRefresh(data.expiresIn ?? DEFAULT_ACCESS_TTL_SEC);
        return true;
      }
      // Only a genuine auth failure ends the session. 401/403 mean the
      // refresh credential itself is missing, expired, or rejected, so the
      // user must log in again. Any other status (500/502/503/504 from the
      // API or a fronting proxy, an unexpected 4xx, etc.) is transient:
      // treating it as terminal logs an actively-working user out on a
      // single blip and drops their unsaved work, while an idle tab that
      // never refreshes survives untouched — the exact asymmetry reported in
      // CU-868kw7906. Keep the session alive and let the next request or the
      // proactive tick retry.
      if (response.status === 401 || response.status === 403) {
        this.markRefreshFailed();
        return false;
      }
      this.recoverFromTransientRefreshFailure();
      return false;
    } catch (err) {
      // A thrown fetch is a network / CORS / preflight failure reaching the
      // refresh endpoint, indistinguishable from the server being briefly
      // unreachable and never proof the session is dead. Same reasoning as
      // the non-401/403 branch above: recover rather than force a logout.
      logger.warn('SessionManager refresh failed transiently; keeping session alive', {
        domain: this._config.domain,
        err,
      });
      this.recoverFromTransientRefreshFailure();
      return false;
    }
  }

  /**
   * Undo the optimistic `Refreshing` hop after a *transient* refresh failure
   * (a network/CORS error, or a non-401/403 status such as a 5xx), returning
   * the machine to `Alive` without emitting `session_expired`. Distinct from
   * markRefreshFailed(), which is reserved for genuine auth failures that
   * truly end the session. Only ever reverses the Alive → Refreshing
   * transition, so it never resurrects a terminal session: a concurrent
   * markRefreshFailed() that already moved us to Dead wins.
   */
  private recoverFromTransientRefreshFailure(): void {
    if (this._state === SessionState.Refreshing) {
      this.setState(SessionState.Alive);
    }
  }

  get state(): SessionState {
    return this._state;
  }

  get domain(): 'user' | 'admin' {
    return this._config.domain;
  }

  /**
   * Boot-time session check. Returns the parsed probe-endpoint body so the
   * caller can extract user/admin identity info from the same round-trip
   * — no second fetch needed.
   *
   * @returns The parsed JSON body on `alive`, `null` on `anonymous` or any
   *   non-2xx / parse / network failure. Callers should also gate on
   *   `session.state === 'alive'` before casting the body, since a 2xx
   *   response with `authenticated: false` (admin pattern) yields
   *   `state === 'anonymous'` and `null`.
   */
  async probe(): Promise<unknown> {
    this.setState(SessionState.Probing);
    try {
      // Boot probe must never read from cache: the prior page's /auth/me
      // response may still be in the HTTP cache and would otherwise leak
      // its (now-stale) currentOrganization through, even when the cookies
      // in the jar are already the new tenant's. Belt-and-suspenders here:
      //   - `cache: 'no-store'` is the standards-compliant signal
      //   - a per-load random query param guarantees a cache miss even
      //     when a browser/proxy/SW ignores Cache-Control
      let response = await fetch(withCacheBuster(this._config.probeEndpoint), {
        method: 'GET',
        credentials: 'include',
        cache: 'no-store',
      });

      // Expired-access-token recovery: the backend router-level resolver
      // raises 401 with detail.error_type === 'expired_token' when the
      // access cookie has elapsed but the refresh cookie may still be
      // valid. This is the normal "returning user" case (e.g. F5 after the
      // access TTL). Without recovery, probe would slide to 'anonymous'
      // and the route guard would redirect to /login despite a usable
      // refresh cookie. Quietly attempt refresh and re-probe; on refresh
      // failure, fall through to the normal non-ok path. Failure here is
      // intentionally silent: probe is boot-time recovery, not an active
      // request, so we do NOT emit session_expired (which would pop a
      // modal on a user who hasn't been told they're logged in yet).
      let recoveredExpiresIn: number | undefined;
      if (response.status === 401) {
        const errorType = await readErrorTypeFromBody(response);
        if (errorType === 'expired_token') {
          const refresh = await this.quietRefresh();
          if (refresh.ok) {
            recoveredExpiresIn = refresh.expiresIn;
            response = await fetch(withCacheBuster(this._config.probeEndpoint), {
              method: 'GET',
              credentials: 'include',
              cache: 'no-store',
            });
          }
        }
      }

      if (!response.ok) {
        this.setState(SessionState.Anonymous);
        return null;
      }

      // Always parse the body. Whether the caller needs identity info
      // (admin / user object) or not, the round-trip is already paid for
      // and parsing exposes it for the caller — eliminating the second
      // GET to the same endpoint that AdminAuthContext / AuthContext used
      // to do.
      const body = (await response.json().catch(() => null)) as unknown;

      // Some probe endpoints (e.g. /api/v1/admin/auth/me) return 200 with
      // `{authenticated: false}` instead of 401 when the caller has no
      // session. The optional isAliveFromBody predicate lets each domain
      // distinguish "really alive" from "200 but anonymous" by inspecting
      // the body. When omitted, any 2xx is treated as alive.
      const alive = this._config.isAliveFromBody ? this._config.isAliveFromBody(body) : true;

      if (alive) {
        this.setState(SessionState.Alive);
        // Only arm the proactive timer when the recovery branch actually
        // refreshed. A normal cookie-valid probe doesn't know the access
        // TTL — the next 401 will trigger reportAnomaly401 → executeRefresh,
        // which schedules the timer on success.
        if (recoveredExpiresIn !== undefined) {
          this.scheduleProactiveRefresh(recoveredExpiresIn);
        }
        return body;
      }

      this.setState(SessionState.Anonymous);
      return null;
    } catch (err) {
      // Network error during boot probe is treated as "no session" — the
      // user can re-probe on next page load.
      logger.warn('SessionManager probe failed; treating session as anonymous', {
        domain: this._config.domain,
        err,
      });
      this.setState(SessionState.Anonymous);
      return null;
    }
  }

  /**
   * Sibling of executeRefresh() used by probe() during boot-time recovery.
   * Same network call, but failures stay quiet — they do NOT call
   * markRefreshFailed() and do NOT emit session_expired. State transitions
   * are left to the caller so probe can decide based on the re-probe result.
   */
  private async quietRefresh(): Promise<{ ok: boolean; expiresIn?: number }> {
    try {
      const response = await fetch(this._config.refreshEndpoint, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        const data = (await response.json().catch(() => ({}))) as {
          expiresIn?: number;
        };
        return { ok: true, expiresIn: data.expiresIn };
      }
      return { ok: false };
    } catch (err) {
      logger.warn('SessionManager quietRefresh threw', {
        domain: this._config.domain,
        err,
      });
      return { ok: false };
    }
  }

  /**
   * Called by the context layer after a successful login response.
   * Transitions to alive and schedules the proactive refresh at 80%
   * of the access-token lifetime.
   */
  loginCompleted(expiresInSec: number): void {
    this.setState(SessionState.Alive);
    this.scheduleProactiveRefresh(expiresInSec);
  }

  /**
   * Called by the context layer after a successful logout. Cancels any
   * pending proactive refresh and transitions to anonymous.
   */
  logoutCompleted(): void {
    this.clearProactiveTimer();
    this.setState(SessionState.Anonymous);
  }

  /**
   * Called by the request layer when a request gets a 401 despite this
   * SessionManager believing the session is alive (clock skew,
   * server-side revocation, race with proactive timer). Single-flight:
   * concurrent callers share the same in-flight refresh promise. Returns
   * true on success, false on failure (state ends 'dead').
   */
  async reportAnomaly401(): Promise<boolean> {
    if (this._state === SessionState.Dead) return false;
    if (this._refreshInFlight !== null) return this._refreshInFlight;

    this._refreshInFlight = this.executeRefresh();
    try {
      return await this._refreshInFlight;
    } finally {
      this._refreshInFlight = null;
    }
  }

  /**
   * Release external bindings (timers, document listener). After destroy
   * the SessionManager is inert; the state machine will not emit further
   * transitions or fire scheduled refreshes. Production code calls this
   * only on HMR; tests call it in afterEach so listeners don't accumulate
   * on `document` across cases.
   */
  destroy(): void {
    this.clearProactiveTimer();
    if (this._visibilityHandler !== null) {
      document.removeEventListener('visibilitychange', this._visibilityHandler);
      this._visibilityHandler = null;
    }
  }

  /**
   * Test-only escape hatch. Returns the SessionManager to its initial
   * state — clears the proactive timer and re-installs a fresh visibility
   * listener so the manager remains live (the previous handler closure is
   * discarded). Use destroy() instead for permanent teardown. Production
   * code calls reset only on HMR; tests call it in afterEach.
   */
  reset(): void {
    this.clearProactiveTimer();
    if (this._visibilityHandler !== null) {
      document.removeEventListener('visibilitychange', this._visibilityHandler);
      this._visibilityHandler = null;
    }
    this.installVisibilityHandler();
    this.setState(SessionState.Unknown);
  }
}
